// This file is auto-generated by @hey-api/openapi-ts

import {
  type Client,
  formDataBodySerializer,
  type Options as Options2,
  type TDataShape,
} from "./client";
import { client } from "./client.gen";
import type {
  AdminActivityActionsData,
  AdminActivityActionsResponses,
  AdminActivityAnalyticsData,
  AdminActivityAnalyticsErrors,
  AdminActivityAnalyticsResponses,
  AdminDeleteUserData,
  AdminDeleteUserErrors,
  AdminDeleteUserResponses,
  AdminFeedbackAnalyticsData,
  AdminFeedbackAnalyticsErrors,
  AdminFeedbackAnalyticsResponses,
  AdminGetUserData,
  AdminGetUserErrors,
  AdminGetUserResponses,
  AdminListActivityData,
  AdminListActivityErrors,
  AdminListActivityResponses,
  AdminListAnswerFeedbackData,
  AdminListAnswerFeedbackErrors,
  AdminListAnswerFeedbackResponses,
  AdminListFeedbackData,
  AdminListFeedbackErrors,
  AdminListFeedbackResponses,
  AdminListUsersData,
  AdminListUsersResponses,
  AdminUpdateUserData,
  AdminUpdateUserErrors,
  AdminUpdateUserResponses,
  CreateChatConversationData,
  CreateChatConversationErrors,
  CreateChatConversationResponses,
  DeleteChatConversationData,
  DeleteChatConversationErrors,
  DeleteChatConversationResponses,
  GetActivityData,
  GetActivityResponses,
  GetChatConversationData,
  GetChatConversationErrors,
  GetChatConversationResponses,
  GetHealthData,
  GetHealthResponses,
  GetMeData,
  GetMeDetailData,
  GetMeDetailResponses,
  GetMeResponses,
  ListChatConversationsData,
  ListChatConversationsResponses,
  LogoutData,
  LogoutResponses,
  SendChatMessageData,
  SendChatMessageErrors,
  SendChatMessageResponses,
  SsoCallbackData,
  SsoCallbackErrors,
  SsoLoginData,
  SsoLoginErrors,
  SubmitSuggestionFeedbackData,
  SubmitSuggestionFeedbackErrors,
  SubmitSuggestionFeedbackResponses,
  UpdateProfileData,
  UpdateProfileErrors,
  UpdateProfileResponses,
} from "./types.gen";

export type Options<
  TData extends TDataShape = TDataShape,
  ThrowOnError extends boolean = boolean,
  TResponse = unknown,
> = Options2<TData, ThrowOnError, TResponse> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client;
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>;
};

/**
 * Sso Login
 *
 * Start the SSO handshake — 302 to the Entra authorize endpoint.
 *
 * Args:
 * next_path: Where to land after sign-in. A **relative path only**; an
 * absolute or protocol-relative value is discarded rather than
 * honoured, because reflecting it into the callback's redirect would
 * be an open redirect off the back of a real sign-in.
 *
 * Returns:
 * A 302 to Entra, carrying the flow cookie — or a 302 to the login page
 * with an error code if SSO is not configured or the discovery document
 * cannot be read.
 */
export const ssoLogin = <ThrowOnError extends boolean = false>(
  options?: Options<SsoLoginData, ThrowOnError>,
) =>
  (options?.client ?? client).get<unknown, SsoLoginErrors, ThrowOnError>({
    url: "/auth/sso/login",
    ...options,
  });

/**
 * Sso Callback
 *
 * Finish the SSO handshake, set the session cookie and 302 into the app.
 *
 * Fails closed at every step: a missing or mismatched `state`, a refused
 * domain, an `id_token` that does not validate against the tenant JWKS, or any
 * unexpected error all redirect to the login page with a code. The flow cookie
 * is cleared on success and on failure alike.
 *
 * Args:
 * request: The incoming request, read for the flow cookie.
 * code: The authorization code from Entra.
 * state: The opaque value echoed back by Entra; must match the cookie.
 * error: Set by Entra when the user cancels or consent is missing.
 *
 * Returns:
 * A 302 to `next` (default `/select-brand`) carrying the session cookie,
 * or a 302 to `/login?error=<code>`.
 */
export const ssoCallback = <ThrowOnError extends boolean = false>(
  options?: Options<SsoCallbackData, ThrowOnError>,
) =>
  (options?.client ?? client).get<unknown, SsoCallbackErrors, ThrowOnError>({
    url: "/auth/sso/callback",
    ...options,
  });

/**
 * Health
 *
 * Return a simple health status payload.
 */
export const getHealth = <ThrowOnError extends boolean = false>(
  options?: Options<GetHealthData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetHealthResponses, unknown, ThrowOnError>({
    url: "/health",
    ...options,
  });

/**
 * Auth Logout
 *
 * Sign out — clear the session cookie.
 */
export const logout = <ThrowOnError extends boolean = false>(
  options?: Options<LogoutData, ThrowOnError>,
) =>
  (options?.client ?? client).post<LogoutResponses, unknown, ThrowOnError>({
    url: "/auth/logout",
    ...options,
  });

/**
 * Auth Me
 *
 * Get the current user's profile.
 */
export const getMe = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeResponses, unknown, ThrowOnError>({
    url: "/auth/me",
    ...options,
  });

/**
 * Auth Me Detail
 *
 * Get the current user's profile with timeline fields.
 */
export const getMeDetail = <ThrowOnError extends boolean = false>(
  options?: Options<GetMeDetailData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetMeDetailResponses, unknown, ThrowOnError>({
    url: "/auth/me/detail",
    ...options,
  });

/**
 * Auth Update Profile
 *
 * Update current user's name.
 */
export const updateProfile = <ThrowOnError extends boolean = false>(
  options: Options<UpdateProfileData, ThrowOnError>,
) =>
  (options.client ?? client).put<UpdateProfileResponses, UpdateProfileErrors, ThrowOnError>({
    url: "/auth/profile",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Auth Activity
 *
 * Get the current user's activity log.
 */
export const getActivity = <ThrowOnError extends boolean = false>(
  options?: Options<GetActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<GetActivityResponses, unknown, ThrowOnError>({
    url: "/auth/activity",
    ...options,
  });

/**
 * Admin List Users
 *
 * List all users (admin only).
 */
export const adminListUsers = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListUsersData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminListUsersResponses, unknown, ThrowOnError>({
    url: "/admin/users",
    ...options,
  });

/**
 * Admin Delete User
 *
 * Delete a user (admin only). Self-deletion blocked.
 *
 * Deletes the `profiles` row, which cascades to the person's activity log,
 * conversations and feedback. This used to delete the GoTrue user instead and
 * let Supabase cascade into `profiles` — which stopped working the moment the
 * data moved, because the profile lives in Azure Postgres and GoTrue's cascade
 * could no longer reach it. The account vanished from Supabase and the row
 * stayed, so the deleted user kept their history and still resolved on
 * lookup.
 */
export const adminDeleteUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminDeleteUserData, ThrowOnError>,
) =>
  (options.client ?? client).delete<AdminDeleteUserResponses, AdminDeleteUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Get User
 *
 * Get a single user detail with activity logs (admin only).
 */
export const adminGetUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminGetUserData, ThrowOnError>,
) =>
  (options.client ?? client).get<AdminGetUserResponses, AdminGetUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
  });

/**
 * Admin Update User
 *
 * Update a user's name or role (admin only). Self-demotion blocked.
 */
export const adminUpdateUser = <ThrowOnError extends boolean = false>(
  options: Options<AdminUpdateUserData, ThrowOnError>,
) =>
  (options.client ?? client).put<AdminUpdateUserResponses, AdminUpdateUserErrors, ThrowOnError>({
    url: "/admin/users/{user_id}",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Admin List Activity
 *
 * List activity across all users (admin only).
 *
 * Search, filtering, sorting, and pagination are all applied server-side by
 * the list_recent_activity SQL function — the client sends params and renders
 * results.
 */
export const adminListActivity = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListActivityData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminListActivityResponses,
    AdminListActivityErrors,
    ThrowOnError
  >({ url: "/admin/activity", ...options });

/**
 * Admin Activity Analytics
 *
 * Aggregated activity analytics for the admin dashboard (admin only).
 */
export const adminActivityAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityAnalyticsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminActivityAnalyticsResponses,
    AdminActivityAnalyticsErrors,
    ThrowOnError
  >({ url: "/admin/activity/analytics", ...options });

/**
 * Admin Activity Actions
 *
 * Distinct activity actions across all time (admin only).
 *
 * Used to populate the activity filter dropdown so it matches the full,
 * unbounded table scope rather than the windowed analytics breakdown.
 */
export const adminActivityActions = <ThrowOnError extends boolean = false>(
  options?: Options<AdminActivityActionsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<AdminActivityActionsResponses, unknown, ThrowOnError>({
    url: "/admin/activity/actions",
    ...options,
  });

/**
 * Admin Feedback Analytics
 *
 * Aggregated suggestion-feedback analytics for the admin dashboard (admin only).
 */
export const adminFeedbackAnalytics = <ThrowOnError extends boolean = false>(
  options?: Options<AdminFeedbackAnalyticsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminFeedbackAnalyticsResponses,
    AdminFeedbackAnalyticsErrors,
    ThrowOnError
  >({ url: "/admin/feedback/analytics", ...options });

/**
 * Admin List Feedback
 *
 * List suggestion feedback across all users (admin only).
 *
 * Search, filtering, sorting, and pagination are all applied server-side by
 * the list_suggestion_feedback SQL function — the client sends params and
 * renders results.
 */
export const adminListFeedback = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListFeedbackData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminListFeedbackResponses,
    AdminListFeedbackErrors,
    ThrowOnError
  >({ url: "/admin/feedback", ...options });

/**
 * Admin List Answer Feedback
 *
 * List Teams' whole-answer ratings across all users (admin only).
 *
 * The `chat_message_feedback` counterpart of `/admin/feedback`, kept as its own
 * route and table so a whole-answer verdict never reaches the per-part accuracy
 * figures. `search` matches the rater's email and name, the comment, and the
 * item id or name of any part on the rated answer — so searching a Part ID
 * finds every Teams rating of an answer that suggested it. Newest verdict
 * first by default: a changed vote is ordered by when it changed.
 */
export const adminListAnswerFeedback = <ThrowOnError extends boolean = false>(
  options?: Options<AdminListAnswerFeedbackData, ThrowOnError>,
) =>
  (options?.client ?? client).get<
    AdminListAnswerFeedbackResponses,
    AdminListAnswerFeedbackErrors,
    ThrowOnError
  >({ url: "/admin/feedback/answers", ...options });

/**
 * List Chat Conversations
 *
 * List the current user's conversations, most recently updated first.
 */
export const listChatConversations = <ThrowOnError extends boolean = false>(
  options?: Options<ListChatConversationsData, ThrowOnError>,
) =>
  (options?.client ?? client).get<ListChatConversationsResponses, unknown, ThrowOnError>({
    url: "/chat/conversations",
    ...options,
  });

/**
 * Create Chat Conversation
 *
 * Create an empty conversation owned by the current user, pinned to a brand.
 *
 * `brand` is required and fixed for the conversation's lifetime: it scopes every
 * machine resolution and BOM search the conversation performs, so one brand's
 * chat can never surface another brand's parts.
 */
export const createChatConversation = <ThrowOnError extends boolean = false>(
  options: Options<CreateChatConversationData, ThrowOnError>,
) =>
  (options.client ?? client).post<
    CreateChatConversationResponses,
    CreateChatConversationErrors,
    ThrowOnError
  >({
    url: "/chat/conversations",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });

/**
 * Delete Chat Conversation
 *
 * Delete a conversation (ownership-checked; 404 if not owned).
 */
export const deleteChatConversation = <ThrowOnError extends boolean = false>(
  options: Options<DeleteChatConversationData, ThrowOnError>,
) =>
  (options.client ?? client).delete<
    DeleteChatConversationResponses,
    DeleteChatConversationErrors,
    ThrowOnError
  >({ url: "/chat/conversations/{conversation_id}", ...options });

/**
 * Get Chat Conversation
 *
 * Get a conversation with its full message history.
 *
 * Returns 404 if the conversation doesn't exist or isn't owned by the
 * current user (ownership failures are folded into 404 to avoid leaking
 * which conversation ids exist).
 */
export const getChatConversation = <ThrowOnError extends boolean = false>(
  options: Options<GetChatConversationData, ThrowOnError>,
) =>
  (options.client ?? client).get<
    GetChatConversationResponses,
    GetChatConversationErrors,
    ThrowOnError
  >({ url: "/chat/conversations/{conversation_id}", ...options });

/**
 * Send Chat Message
 *
 * Send a message (text and/or files) and stream the agentic matching turn.
 *
 * Returns an SSE (`text/event-stream`) response. The model drives retrieval
 * itself via tools; events are emitted as `event: <type>\ndata: <json>`:
 * `meta` (conversation id + persisted user message), `text` (assistant prose
 * deltas), `tool_use` / `tool_result` (retrieval steps), `done` (persisted
 * assistant message + updated conversation), `error` (on failure).
 *
 * At least one of `content` or `files` must be present (400 otherwise).
 *
 * When `machine_id` is provided it is an explicit, trusted machine choice from
 * the client (the user clicked a candidate the backend itself offered). It is
 * validated against the `machines` table (404 if it doesn't exist, 400 if it
 * belongs to a brand other than the conversation's) and used to resolve the
 * conversation directly, bypassing the engine's fuzzy machine resolution for
 * this turn.
 */
export const sendChatMessage = <ThrowOnError extends boolean = false>(
  options: Options<SendChatMessageData, ThrowOnError>,
) =>
  (options.client ?? client).post<SendChatMessageResponses, SendChatMessageErrors, ThrowOnError>({
    ...formDataBodySerializer,
    url: "/chat/conversations/{conversation_id}/messages",
    ...options,
    headers: {
      "Content-Type": null,
      ...options.headers,
    },
  });

/**
 * Submit Suggestion Feedback
 *
 * Mark one suggested part on a message as correct or incorrect.
 *
 * Any suggestion — the top pick or an alternative — can be thumbed up/down.
 * The (item_id, requested_item) pair identifies the exact stored suggestion.
 *
 * Returns the affected PartSuggestion with `feedback` populated so the frontend
 * can update local state without a full refetch. `figure` is left null here:
 * the client merges only the feedback value out of this response (it already
 * holds the figure from the conversation read), so re-resolving the drawing
 * would cost a query and two signed URLs for a value nothing consumes.
 *
 * Behavior:
 * - 404 if the message doesn't exist or its conversation isn't owned by the
 * current user (ownership failures fold into 404 to avoid leaking ids).
 * - 404 if (item_id, requested_item) isn't a real entry in the message's
 * stored suggestions, so feedback can't be attached to fabricated entries.
 * - Upserts on (message_id, item_id, requested_item, user_id) so resubmitting
 * updates the existing row (and its updated_at) rather than duplicating.
 */
export const submitSuggestionFeedback = <ThrowOnError extends boolean = false>(
  options: Options<SubmitSuggestionFeedbackData, ThrowOnError>,
) =>
  (options.client ?? client).put<
    SubmitSuggestionFeedbackResponses,
    SubmitSuggestionFeedbackErrors,
    ThrowOnError
  >({
    url: "/chat/messages/{message_id}/feedback",
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...options.headers,
    },
  });
